(() => {
  const tagName = 'cad-product-hero';

  if (!globalThis.customElements || customElements.get(tagName)) return;

  class CadProductHero extends HTMLElement {
    constructor() {
      super();
      this.connectionController = null;
      this.widget = null;
      this.panels = [];
      this.tabs = [];
      this.liveRegion = null;
      this.activeModelId = null;
      this.loadingModelId = null;
      this.mobileQuery = null;
      this.initialModelRandomized = false;
    }

    connectedCallback() {
      this.connectionController?.abort();
      this.connectionController = new AbortController();
      const { signal } = this.connectionController;

      this.widget = this.querySelector('cad-wireframe');
      this.panels = Array.from(this.querySelectorAll('[data-cad-panel]'));
      this.tabs = Array.from(this.querySelectorAll('[data-cad-tab]'));
      this.liveRegion = this.querySelector('[data-cad-live-region]');
      this.mobileQuery = globalThis.matchMedia?.('(max-width: 749px)') || null;
      this.randomizeInitialModel();

      const syncViewerInteraction = () => this.syncViewerInteraction();
      this.mobileQuery?.addEventListener('change', syncViewerInteraction, { signal });
      if (this.widget?.shadowRoot) {
        syncViewerInteraction();
      } else if (this.widget && globalThis.customElements) {
        void customElements.whenDefined('cad-wireframe').then(() => {
          if (!signal.aborted) syncViewerInteraction();
        });
      }

      this.tabs.forEach((tab) => {
        tab.addEventListener(
          'click',
          () => {
            const modelId = this.modelIdFor(tab);
            if (modelId) void this.selectModel(modelId);
          },
          { signal }
        );
      });

      this.widget?.addEventListener(
        'cad-load-start',
        (event) => {
          this.loadingModelId = this.modelIdFromEvent(event) || this.widget?.getAttribute('model');
          this.setLoading(true);
        },
        { signal }
      );

      this.widget?.addEventListener(
        'cad-model-change',
        (event) => {
          const modelId = this.modelIdFromEvent(event) || this.widget?.getAttribute('model');
          this.setLoading(false);
          this.loadingModelId = null;
          if (modelId) this.synchronize(modelId);
          this.syncViewerInteraction();
        },
        { signal }
      );

      this.widget?.addEventListener(
        'cad-error',
        (event) => {
          const modelId = this.modelIdFromEvent(event) || this.loadingModelId;
          this.setLoading(false);
          this.loadingModelId = null;
          this.announceError(modelId);
        },
        { signal }
      );

      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const target = event.target;
          if (!(target instanceof Element) || !this.contains(target)) return;

          const marker = target.closest(
            '[data-cad-panel], [data-cad-editor-model], [data-model-id], [data-cad-model-id]'
          );
          if (!marker || !this.contains(marker)) return;

          const panel = marker.matches('[data-cad-panel]') ? marker : marker.closest('[data-cad-panel]');
          const modelId = this.modelIdFor(marker) || this.modelIdFor(panel);
          if (modelId) void this.selectModel(modelId);
        },
        { signal }
      );

      const initialModelId =
        this.widget?.activeModelId ||
        this.widget?.getAttribute('model') ||
        this.modelIdFor(this.panels[0]) ||
        this.modelIdFor(this.tabs[0]);

      this.setLoading(false);
      if (initialModelId) this.synchronize(initialModelId);
    }

    disconnectedCallback() {
      this.connectionController?.abort();
      this.connectionController = null;
      this.widget = null;
      this.panels = [];
      this.tabs = [];
      this.liveRegion = null;
      this.loadingModelId = null;
      this.mobileQuery = null;
    }

    async selectModel(modelId) {
      try {
        if (!this.widget) throw new Error('CAD wireframe widget is unavailable.');

        if (typeof this.widget.selectModel !== 'function' && globalThis.customElements) {
          await customElements.whenDefined('cad-wireframe');
        }

        if (!this.isConnected || typeof this.widget?.selectModel !== 'function') {
          throw new Error('CAD wireframe widget is unavailable.');
        }

        await this.widget.selectModel(modelId);
      } catch (error) {
        this.setLoading(false);
        console.error(`Unable to select CAD model "${modelId}".`, error);
      }
    }

    synchronize(modelId) {
      this.activeModelId = modelId;

      let activePanel = null;
      this.panels.forEach((panel) => {
        const isActive = this.modelIdFor(panel) === modelId;
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', String(!isActive));
        if (isActive) activePanel = panel;
      });

      let activeTab = null;
      this.tabs.forEach((tab) => {
        const isActive = this.modelIdFor(tab) === modelId;
        if (isActive) {
          tab.setAttribute('aria-current', 'true');
          activeTab = tab;
        } else {
          tab.removeAttribute('aria-current');
        }
      });

      this.announceModel(modelId, activePanel, activeTab);
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.toggleAttribute('data-loading', isLoading);
      this.setAttribute('aria-busy', String(isLoading));
    }

    syncViewerInteraction() {
      const canvas = this.widget?.shadowRoot?.querySelector('[data-cad-canvas]');
      if (!canvas) return;

      const isDecorative = Boolean(this.mobileQuery?.matches);
      canvas.tabIndex = isDecorative ? -1 : 0;
      canvas.toggleAttribute('aria-hidden', isDecorative);
    }

    announceModel(modelId, panel, tab) {
      if (!this.liveRegion) return;
      const label = this.modelLabel(modelId, panel, tab);
      this.liveRegion.textContent = `Showing ${label}`;
    }

    announceError(modelId) {
      if (!this.liveRegion) return;
      const label = this.modelLabel(modelId);
      this.liveRegion.textContent = label ? `Unable to load ${label}` : 'Unable to load CAD model';
    }

    modelLabel(modelId, panel = null, tab = null) {
      const matchingPanel =
        panel || this.panels.find((candidate) => this.modelIdFor(candidate) === modelId) || null;
      const matchingTab = tab || this.tabs.find((candidate) => this.modelIdFor(candidate) === modelId) || null;
      const heading = matchingPanel?.querySelector('[data-cad-title], h1, h2, h3');

      return (
        matchingTab?.getAttribute('data-cad-label')?.trim() ||
        matchingPanel?.getAttribute('data-cad-label')?.trim() ||
        matchingTab?.getAttribute('aria-label')?.trim() ||
        heading?.textContent?.trim() ||
        matchingTab?.textContent?.trim() ||
        modelId ||
        'CAD model'
      );
    }

    modelIdFromEvent(event) {
      return event.detail?.model?.id || event.detail?.modelId || event.detail?.id || null;
    }

    randomizeInitialModel() {
      if (this.initialModelRandomized || !this.widget) return;

      const modelIds = [
        ...new Set(this.panels.map((panel) => this.modelIdFor(panel)).filter(Boolean)),
      ];
      if (!modelIds.length) return;

      const randomIndex = Math.floor(Math.random() * modelIds.length);
      this.widget.setAttribute('model', modelIds[randomIndex]);
      this.initialModelRandomized = true;
    }

    modelIdFor(element) {
      if (!element) return null;

      const candidates = [
        element.getAttribute('data-model-id'),
        element.getAttribute('data-cad-model-id'),
        element.getAttribute('data-cad-model'),
        element.getAttribute('data-model'),
        element.getAttribute('data-cad-editor-model'),
        element.getAttribute('data-cad-panel'),
        element.getAttribute('data-cad-tab'),
      ];

      return candidates.find((value) => value?.trim())?.trim() || null;
    }
  }

  customElements.define(tagName, CadProductHero);
})();
